import React from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  Button,
} from '@mui/material';
import {
  ExpandMore,
  Gavel,
  School,
  Payment,
  Cancel,
  Warning,
  Shield,
  Description,
  ContactMail,
  Phone,
  Update,
  DirectionsCar,
  VerifiedUser,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
  const navigate = useNavigate();

  const sections = [
    {
      title: "Acceptance of Terms",
      icon: <Gavel />,
      content: [
        "By enrolling in Moxy Driving School, accessing our website, or using our mobile application, you agree to be bound by these Terms of Service",
        "If you do not agree to these terms, you must not use our services",
        "These terms apply to all students, parents/guardians, instructors, and visitors",
        "We reserve the right to modify these terms at any time with prior notice",
        "Continued use of our services after changes constitutes acceptance of modified terms",
        "Students under 18 must have parent/guardian consent to enroll and use our services"
      ]
    },
    {
      title: "Enrollment and Eligibility",
      icon: <School />,
      content: [
        "Minimum age requirement: 18 years for full course enrollment (16 years for learner's permit programs with parental consent)",
        "Valid identification required: National ID, Passport, or Birth Certificate",
        "Medical fitness: Students must be medically fit to drive (medical certificate may be required)",
        "Valid learner's permit or license (depending on course level)",
        "Enrollment is subject to availability and acceptance by Moxy Driving School",
        "We reserve the right to refuse enrollment for safety or legal reasons",
        "All information provided during enrollment must be accurate and truthful",
        "Foreign students must have valid visas and work/study permits as required by Kenyan law"
      ]
    },
    {
      title: "Courses and Training",
      icon: <DirectionsCar />,
      content: [
        "Course duration and content are as described in the enrollment agreement",
        "Practical lessons are subject to instructor availability and weather conditions",
        "Theory classes must be completed before practical road lessons (as per NTSA requirements)",
        "Students must attend all scheduled classes and practical sessions",
        "Minimum attendance requirement: 80% of theory classes and all practical lessons",
        "Course completion does not guarantee passing the NTSA driving test",
        "We provide training to NTSA standards but cannot guarantee test results",
        "Additional lessons may be required based on individual progress (at standard rates)",
        "Course validity: 6 months from enrollment date (extensions subject to additional fees)"
      ]
    },
    {
      title: "Fees and Payment Terms",
      icon: <Payment />,
      content: [
        "All fees are quoted in Kenyan Shillings (KES) and are subject to change with notice",
        "Full payment or deposit (50%) required at enrollment to secure your spot",
        "Accepted payment methods: M-Pesa, Bank Transfer, Paystack, Cash",
        "Payment plans available: Contact admin for installment options",
        "All fees include: Training materials, vehicle use, instructor fees, and fuel",
        "NOT included: NTSA test fees, license application fees, medical examination fees",
        "Late payment fee: 5% of outstanding amount after 7 days past due date",
        "Failure to complete payment may result in suspension of lessons",
        "Receipts issued for all payments (electronic or physical)",
        "No refunds for completed lessons or materials provided"
      ]
    },
    {
      title: "Cancellation and Refund Policy",
      icon: <Cancel />,
      content: [
        "Cancellation before course start: 90% refund (10% admin fee)",
        "Cancellation within first week: 70% refund (minus completed lessons)",
        "Cancellation after 2 weeks: 50% refund (minus completed lessons)",
        "No refunds after 50% course completion",
        "Lesson rescheduling: Free if done 24 hours in advance",
        "Late cancellation (less than 24 hours): Lesson fee forfeited",
        "No-show without notice: Lesson fee forfeited",
        "Course suspension: Students may suspend for up to 3 months (once) for medical/emergency reasons with documentation",
        "Refunds processed within 30 business days via original payment method",
        "Refund requests must be submitted in writing to admin@moxydrivingschool.co.ke"
      ]
    },
    {
      title: "Student Responsibilities and Conduct",
      icon: <VerifiedUser />,
      content: [
        "Arrive on time for all lessons (15-minute grace period, then lesson may be forfeited)",
        "Bring valid learner's permit/license to all practical lessons",
        "Be sober and drug-free during all lessons (zero tolerance policy)",
        "Follow all instructor directions and safety protocols",
        "Treat instructors, staff, and vehicles with respect",
        "Report any vehicle defects or safety concerns immediately",
        "No smoking, eating, or drinking in training vehicles",
        "Maintain proper hygiene and appropriate dress code",
        "No use of mobile phones during driving lessons (emergency calls only, with instructor approval)",
        "Students are liable for damage caused by negligence or misconduct",
        "Harassment, discrimination, or abusive behavior will result in immediate expulsion without refund",
        "Follow all Kenyan traffic laws and NTSA regulations"
      ]
    },
    {
      title: "Safety and Liability",
      icon: <Shield />,
      content: [
        "All training vehicles are insured and maintained to NTSA standards",
        "Dual control vehicles used for practical training",
        "Comprehensive insurance covers students during lessons (subject to terms)",
        "Students are NOT covered for willful misconduct or gross negligence",
        "Moxy Driving School is NOT liable for injuries due to student negligence",
        "Students must disclose any medical conditions that may affect driving ability",
        "In case of accident during lesson: Instructor will handle all procedures and notifications",
        "Students may be liable for insurance excess/deductibles in case of at-fault accidents",
        "Emergency procedures are explained during first lesson",
        "We reserve the right to refuse lessons if student appears unfit to drive (refunded)",
        "COVID-19 Safety: Students showing symptoms will be asked to reschedule (no penalty)"
      ]
    },
    {
      title: "Intellectual Property",
      icon: <Description />,
      content: [
        "All training materials, curriculum, and content are proprietary to Moxy Driving School",
        "Students may use materials for personal learning only",
        "Reproduction, distribution, or commercial use of materials is prohibited",
        "Course materials include: Manuals, videos, presentations, and online resources",
        "Copyright infringement will result in legal action",
        "Moxy Driving School logo and branding are registered trademarks",
        "Student testimonials and photos may be used for marketing (with consent)"
      ]
    },
    {
      title: "Mobile Application and Website Use",
      icon: <Phone />,
      content: [
        "Account security: You are responsible for maintaining confidentiality of login credentials",
        "One account per student - sharing accounts is prohibited",
        "App must be used in accordance with our Privacy Policy",
        "We reserve the right to suspend or terminate accounts for misuse",
        "Booking lessons via app: Confirmations are binding commitments",
        "Technical issues: Report to support@moxydrivingschool.co.ke immediately",
        "Service interruptions may occur for maintenance (with advance notice when possible)",
        "Minimum system requirements: Android 6.0+, iOS 12+, modern web browsers",
        "Data usage and charges are your responsibility",
        "No hacking, scraping, or unauthorized access attempts (subject to legal action)"
      ]
    },
    {
      title: "NTSA and Legal Compliance",
      icon: <Warning />,
      content: [
        "All training complies with National Transport and Safety Authority (NTSA) regulations",
        "Students must obtain learner's permit from NTSA before practical lessons",
        "We facilitate but do not administer NTSA driving tests",
        "NTSA test booking and fees are separate from course fees",
        "Students are responsible for meeting all NTSA requirements",
        "We report serious violations to NTSA as required by law",
        "Course certificates issued upon successful completion (not NTSA license)",
        "License application is student's responsibility after passing NTSA test"
      ]
    },
    {
      title: "Termination of Services",
      icon: <Cancel />,
      content: [
        "We reserve the right to terminate services immediately for:",
        "• Violation of these Terms of Service",
        "• Abusive or threatening behavior towards staff",
        "• Fraud or providing false information",
        "• Criminal activity or safety violations",
        "• Consistent no-shows or payment defaults",
        "Termination for cause: No refund provided",
        "Students may terminate at any time subject to refund policy",
        "Upon termination, access to app, materials, and services is revoked immediately"
      ]
    },
    {
      title: "Dispute Resolution",
      icon: <Gavel />,
      content: [
        "Informal resolution: Contact us first to resolve any disputes",
        "Mediation: Disputes will be referred to mediation before legal action",
        "Jurisdiction: These terms are governed by the laws of Kenya",
        "Legal venue: Kiambu Law Courts, Kenya",
        "Arbitration option: Parties may agree to binding arbitration",
        "Class action waiver: Disputes must be resolved individually, not as class actions",
        "Legal fees: Prevailing party may recover reasonable legal fees",
        "No waiver: Failure to enforce any term does not waive our right to enforce it later"
      ]
    },
    {
      title: "Privacy and Data Protection",
      icon: <Shield />,
      content: [
        "Your privacy is governed by our Privacy Policy (read at /privacy-policy)",
        "By using our services, you consent to data collection as described in Privacy Policy",
        "We comply with Kenya Data Protection Act, 2019 and GDPR",
        "You have the right to access, correct, or delete your personal data",
        "Data retention: 7 years for academic/financial records (legal requirement)",
        "Third-party sharing: Only with your consent or as required by law",
        "Security measures: Encryption, secure servers, access controls",
        "Data breach notification: Within 72 hours if your data is compromised"
      ]
    },
    {
      title: "Force Majeure",
      icon: <Warning />,
      content: [
        "We are not liable for delays or failures due to circumstances beyond our control:",
        "• Natural disasters (floods, earthquakes, pandemics)",
        "• Government actions or restrictions",
        "• Strikes or labor disputes",
        "• War, terrorism, or civil unrest",
        "• Vehicle breakdown or accidents not due to negligence",
        "• Power outages or technical failures",
        "In such cases, lessons will be rescheduled without penalty",
        "Refunds not provided for force majeure events",
        "We will make reasonable efforts to minimize disruption and notify students promptly"
      ]
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 3, md: 5 },
          borderRadius: 3,
          background: 'linear-gradient(135deg, #fff9f0 0%, #ffe8cc 100%)'
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Box
            sx={{
              display: 'inline-flex',
              p: 2,
              borderRadius: '50%',
              bgcolor: '#ffcc80',
              mb: 3
            }}
          >
            <Gavel sx={{ fontSize: 40, color: '#e65100' }} />
          </Box>
          <Typography 
            variant="h3" 
            gutterBottom 
            sx={{ 
              fontWeight: 700,
              color: 'warning.dark',
              fontSize: { xs: '2rem', md: '3rem' }
            }}
          >
            Terms of Service
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ maxWidth: 600, mx: 'auto' }}
          >
            Moxy Driving School - Terms and Conditions
          </Typography>
        </Box>

        {/* Last Updated */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 4,
            p: 2,
            bgcolor: '#e1f5fe',
            borderRadius: 2,
            color: '#01579b'
          }}
        >
          <Update sx={{ mr: 1 }} />
          <Typography variant="body1" fontWeight={600}>
            Last Updated: January 2025 | Effective Date: January 1, 2025
          </Typography>
        </Box>

        {/* Important Notice */}
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 4 }}>
          <Typography variant="body1" fontWeight={600} gutterBottom>
            PLEASE READ THESE TERMS CAREFULLY
          </Typography>
          <Typography variant="body2">
            These Terms of Service constitute a legally binding agreement between you and 
            Moxy Driving School. By enrolling in our courses or using our services, you 
            acknowledge that you have read, understood, and agree to be bound by these terms.
          </Typography>
        </Alert>

        {/* Introduction */}
        <Paper sx={{ p: 3, mb: 4, bgcolor: '#fff3e0', color: '#e65100' }}>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Welcome to Moxy Driving School
          </Typography>
          <Typography variant="body1" paragraph>
            These Terms of Service ("Terms") govern your use of Moxy Driving School's services, 
            including but not limited to driving instruction, theory classes, mobile application, 
            website, and all related services (collectively, the "Services").
          </Typography>
          <Typography variant="body1" paragraph>
            Moxy Driving School ("we," "us," or "our") is a registered driving school operating 
            in Kenya, located at Ruiru Bypass, Kiambu County. We are licensed by the National 
            Transport and Safety Authority (NTSA) to provide driving instruction.
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            By using our Services, you agree to comply with these Terms, our Privacy Policy, 
            and all applicable laws and regulations of Kenya.
          </Typography>
        </Paper>

        {/* Quick Actions */}
        <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/privacy_policy')}
          >
            Read Privacy Policy
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/delete_data')}
          >
            Request Data Deletion
          </Button>
        </Box>

        {/* Main Sections */}
        <Box sx={{ mb: 4 }}>
          {sections.map((section, index) => (
            <Accordion 
              key={index}
              sx={{ 
                mb: 2,
                borderRadius: 2,
                '&:before': { display: 'none' },
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMore />}
                sx={{ 
                  bgcolor: 'grey.50',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ mr: 2, color: 'warning.main' }}>
                    {section.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={600}>
                    {section.title}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <List>
                  {section.content.map((item, itemIndex) => (
                    <ListItem key={itemIndex} sx={{ pl: 0, alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 35, mt: 0.5 }}>
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: 'warning.main'
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText 
                        primary={item}
                        sx={{ 
                          '& .MuiListItemText-primary': { 
                            fontSize: '0.95rem',
                            lineHeight: 1.6
                          }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Additional Important Information */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight={600} color="warning.dark">
            Limitation of Liability
          </Typography>
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="body1" paragraph>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Moxy Driving School shall not be liable for any indirect, incidental, special, consequential, or punitive damages"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Total liability for any claim shall not exceed the total fees paid by the student for the course"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="We are not liable for student performance on NTSA tests or license applications"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="We are not liable for loss of time, income, or opportunities due to course scheduling"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="We maintain appropriate insurance but students may be liable for damages caused by their negligence"
                />
              </ListItem>
            </List>
          </Paper>

          <Typography variant="h5" gutterBottom fontWeight={600} color="warning.dark">
            Warranties and Disclaimers
          </Typography>
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="body1" paragraph fontWeight={600}>
              SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE"
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="We do not guarantee uninterrupted, timely, secure, or error-free service"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="We do not warrant that students will pass NTSA tests or obtain licenses"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Training quality meets NTSA standards but results depend on individual effort"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Information provided is believed accurate but may contain errors"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="We disclaim all warranties, express or implied, including merchantability and fitness for particular purpose"
                />
              </ListItem>
            </List>
          </Paper>

          <Typography variant="h5" gutterBottom fontWeight={600} color="warning.dark">
            Indemnification
          </Typography>
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="body1" paragraph>
              You agree to indemnify, defend, and hold harmless Moxy Driving School, its owners, 
              instructors, employees, and agents from any claims, damages, losses, liabilities, 
              and expenses (including legal fees) arising from:
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Your violation of these Terms" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Your negligence or willful misconduct during lessons" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Your violation of any laws or regulations" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Your infringement of any third-party rights" />
              </ListItem>
            </List>
          </Paper>

          <Typography variant="h5" gutterBottom fontWeight={600} color="warning.dark">
            Severability
          </Typography>
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="body1">
              If any provision of these Terms is found to be invalid, illegal, or unenforceable, 
              the remaining provisions shall continue in full force and effect. The invalid 
              provision shall be replaced with a valid provision that most closely matches the 
              intent of the original provision.
            </Typography>
          </Paper>

          <Typography variant="h5" gutterBottom fontWeight={600} color="warning.dark">
            Entire Agreement
          </Typography>
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="body1">
              These Terms of Service, together with our Privacy Policy and any enrollment 
              agreements, constitute the entire agreement between you and Moxy Driving School 
              regarding the use of our Services. They supersede all prior or contemporaneous 
              communications, agreements, or understandings, whether written or oral.
            </Typography>
          </Paper>

          <Typography variant="h5" gutterBottom fontWeight={600} color="warning.dark">
            Assignment
          </Typography>
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="body1">
              You may not assign or transfer these Terms or your enrollment without our prior 
              written consent. We may assign these Terms to any affiliate or successor without 
              your consent. Any attempted assignment in violation of this provision is void.
            </Typography>
          </Paper>

          <Typography variant="h5" gutterBottom fontWeight={600} color="warning.dark">
            Contact for Legal Notices
          </Typography>
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="body1" paragraph>
              All legal notices, including complaints, arbitration notices, or service of 
              process, should be sent to:
            </Typography>
            <Typography variant="body1" component="div">
              <strong>Legal Department</strong><br />
              Moxy Driving School<br />
              Ruiru Bypass<br />
              Kiambu County, Kenya<br />
              Email: legal@moxydrivingschool.co.ke<br />
              Phone: +254 728 018 513
            </Typography>
          </Paper>
        </Box>

        {/* Acknowledgment Section */}
        <Paper
          sx={{
            p: 4,
            bgcolor: '#ffebee',
            color: '#c62828',
            borderRadius: 3,
            mb: 4
          }}
        >
          <Typography variant="h5" gutterBottom fontWeight={600} textAlign="center">
            Student Acknowledgment
          </Typography>
          <Typography variant="body1" textAlign="center" paragraph>
            BY ENROLLING IN MOXY DRIVING SCHOOL OR USING OUR SERVICES, YOU ACKNOWLEDGE THAT:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="✓ You have read and understood these Terms of Service in their entirety"
                primaryTypographyProps={{ textAlign: 'center', fontWeight: 600 }}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="✓ You agree to be bound by these Terms and our Privacy Policy"
                primaryTypographyProps={{ textAlign: 'center', fontWeight: 600 }}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="✓ You have had the opportunity to ask questions and seek clarification"
                primaryTypographyProps={{ textAlign: 'center', fontWeight: 600 }}
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="✓ You consent to electronic communications and digital signatures"
                primaryTypographyProps={{ textAlign: 'center', fontWeight: 600 }}
              />
            </ListItem>
          </List>
        </Paper>

        {/* Contact Information */}
        <Paper
          sx={{
            p: 4,
            bgcolor: '#ffe0b2',
            color: '#e65100',
            borderRadius: 3,
            textAlign: 'center'
          }}
        >
          <ContactMail sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Questions About These Terms?
          </Typography>
          <Typography variant="body1" paragraph>
            Our team is here to help clarify any provisions or answer your questions
          </Typography>
          
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'center',
              alignItems: 'center',
              gap: 3,
              mt: 3
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ContactMail sx={{ mr: 1 }} />
              <Typography>legal@moxydrivingschool.co.ke</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Phone sx={{ mr: 1 }} />
              <Typography>+254 728 018 513</Typography>
            </Box>
          </Box>
          
          <Divider sx={{ my: 3, bgcolor: 'warning.contrastText', opacity: 0.3 }} />
          
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            <strong>Moxy Driving School</strong><br />
            Ruiru Bypass, Kiambu County, Kenya<br />
            NTSA Licensed Driving School<br />
            Business Hours: Monday - Saturday, 7:00 AM - 6:00 PM
          </Typography>
        </Paper>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'grey.200' }}>
          <Typography variant="body2" color="text.secondary" textAlign="center" paragraph>
            These Terms of Service are governed by and construed in accordance with the laws of the 
            Republic of Kenya. Any disputes shall be subject to the exclusive jurisdiction of the 
            Kenyan courts.
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" fontWeight={600}>
            © {new Date().getFullYear()} Moxy Driving School. All Rights Reserved.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default TermsOfService;